import { useId, useState } from 'react';
import { CloseIcon } from '../icons';

/**
 * Free-form chips. Tags are normalised server-side (trimmed, lowercased,
 * de-duplicated), and the same rules are applied here so what you type is what
 * you see saved.
 */
export function TagInput({
  id,
  tags,
  suggestions,
  compact = false,
  onChange,
}: {
  /** So a surrounding `<label>` points at the box people type in. */
  id?: string;
  tags: string[];
  suggestions: string[];
  /** Matches the `compact` on the ui field primitives, so this box is the same
   *  height as the selects it sits under in the task panel's property sheet. */
  compact?: boolean;
  onChange: (tags: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  const listId = useId();

  function commit(raw: string) {
    const tag = raw.trim().toLowerCase();
    setDraft('');
    if (!tag || tags.includes(tag)) return;
    onChange([...tags, tag]);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === ',') {
      // Enter would otherwise submit the surrounding form.
      event.preventDefault();
      commit(draft);
    } else if (event.key === 'Backspace' && draft === '' && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 rounded-lg border border-stone-300 bg-white shadow-sm transition focus-within:border-honey-500 focus-within:ring-2 focus-within:ring-honey-500/30 ${
        compact ? 'px-1.5 py-1' : 'px-2 py-1.5'
      }`}
    >
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 rounded-full bg-stone-100 py-0.5 pl-2 pr-1 text-xs font-medium text-stone-700"
        >
          {tag}
          <button
            type="button"
            onClick={() => onChange(tags.filter((entry) => entry !== tag))}
            aria-label={`Remove ${tag}`}
            className="rounded-full px-1 text-stone-500 hover:bg-stone-200 hover:text-stone-900"
          >
            <CloseIcon className="h-3 w-3" />
          </button>
        </span>
      ))}

      <input
        id={id}
        list={listId}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        // Losing focus with text in the box should not silently discard it.
        onBlur={() => commit(draft)}
        placeholder={tags.length === 0 ? 'Add a tag, then press Enter' : ''}
        className={`min-w-[10rem] flex-1 border-0 bg-transparent px-1 py-0.5 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-0 ${
          compact ? 'text-dense' : 'text-sm'
        }`}
      />

      <datalist id={listId}>
        {suggestions
          .filter((tag) => !tags.includes(tag))
          .map((tag) => (
            <option key={tag} value={tag} />
          ))}
      </datalist>
    </div>
  );
}
